/**
 * Time-indexed event ledger projected from the runtime WebSocket feed.
 * Used by the Engineering Clock when syncAll is active.
 */

import { create } from 'zustand';
import type { EngineeringLedgerEvent, LifecycleStageId } from './types';
import type { EngineeringWsMessage } from '../types/engineering';

function stageFromEventType(type: string): LifecycleStageId | undefined {
  const t = type.toLowerCase();
  if (t.includes('ingest') || t.includes('scan')) return 'scan';
  if (t.includes('incident')) return 'incident';
  if (t.includes('planner') || t.includes('plan')) return 'planning';
  if (t.includes('agent.') || t.includes('worker') || t.includes('orchestrator')) return 'workers';
  if (t.includes('executor') || t.includes('mutation') || t.includes('engineering')) return 'execution';
  if (t.includes('validation') || t.includes('verify')) return 'verification';
  if (t.includes('replay')) return 'replay';
  if (t.includes('deployment') || t.includes('deploy')) return 'deployment';
  if (t.includes('promotion') || t.includes('promot')) return 'promotion';
  if (t.includes('learning') || t.includes('episode') || t.includes('knowledge')) return 'learning';
  if (t.includes('mission_decision') || t.includes('approval')) return 'approval';
  return undefined;
}

function channelFromType(type: string): EngineeringLedgerEvent['channel'] {
  const t = type.toLowerCase();
  if (t.includes('replay')) return 'replay';
  if (t.includes('validat') || t.includes('verify')) return 'verify';
  if (t.includes('deploy') || t.includes('promot')) return 'deploy';
  if (t.includes('agent') || t.includes('worker') || t.includes('orchestrator')) return 'workers';
  if (t.includes('operational') || t.includes('system')) return 'system';
  return 'runtime';
}

function toLedgerEvent(msg: EngineeringWsMessage, index: number): EngineeringLedgerEvent {
  const payload = msg.payload ?? {};
  const entityIds: string[] = [];
  for (const key of ['incident_id', 'session_id', 'task_id', 'file', 'path', 'worker_id', 'agent_role']) {
    const v = payload[key];
    if (typeof v === 'string' && v) entityIds.push(v);
  }
  return {
    id: `evt-${msg.timestamp}-${index}-${msg.type}`,
    t: msg.timestamp,
    type: msg.type,
    stageId: stageFromEventType(msg.type),
    entityIds,
    summary: String(payload.detail ?? payload.message ?? payload.reasoning ?? payload.task ?? msg.type),
    channel: channelFromType(msg.type),
    payload,
  };
}

interface EventLedgerState {
  events: EngineeringLedgerEvent[];
  ingestWsMessages: (messages: EngineeringWsMessage[]) => void;
  append: (event: EngineeringLedgerEvent) => void;
  clear: () => void;
  /** Events at or before playhead (inclusive) */
  eventsAtOrBefore: (playhead: string | null) => EngineeringLedgerEvent[];
  /** Nearest event to playhead */
  nearest: (playhead: string | null) => EngineeringLedgerEvent | null;
}

export const useEventLedger = create<EventLedgerState>((set, get) => ({
  events: [],

  ingestWsMessages: (messages) => {
    const events = messages.map((m, i) => toLedgerEvent(m, i));
    // Newest first in WS store; keep chronological ascending for scrubbing
    const sorted = [...events].sort((a, b) => a.t.localeCompare(b.t));
    set({ events: sorted.slice(-500) });
  },

  append: (event) =>
    set((s) => ({
      events: [...s.events, event].slice(-500),
    })),

  clear: () => set({ events: [] }),

  eventsAtOrBefore: (playhead) => {
    const { events } = get();
    if (!playhead) return events;
    return events.filter((e) => e.t <= playhead);
  },

  nearest: (playhead) => {
    const { events } = get();
    if (!events.length) return null;
    if (!playhead) return events[events.length - 1];
    let best = events[0];
    let bestDelta = Math.abs(Date.parse(best.t) - Date.parse(playhead));
    for (const e of events) {
      const d = Math.abs(Date.parse(e.t) - Date.parse(playhead));
      if (d < bestDelta) {
        best = e;
        bestDelta = d;
      }
    }
    return best;
  },
}));
